import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { loadMarketingPixels } from "../../utils/pixels";

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setTimeout(() => setShow(true), 500);
    }
  }, []);

  const saveConsent = (necessary, statistics, marketing) => {
    const consent = {
      necessary: necessary,
      statistics: statistics,
      marketing: marketing,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setShow(false);

    if (marketing) {
      loadMarketingPixels();
    }

    console.log("[Cookie Consent] Saved:", consent);
  };

  const handleAcceptAll = () => {
    saveConsent(true, true, true);
  };

  const handleRejectAll = () => {
    saveConsent(true, false, false);
  };

  const handleSaveCustom = () => {
    saveConsent(true, false, marketingChecked);
  };

  useEffect(() => {
    window.showCookieSettings = () => {
      localStorage.removeItem("cookie_consent");
      setShow(true);
    };
  }, []);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-hidden">
        {/* Brand accent bar */}
        <div className="h-1 bg-gradient-to-r from-brand-400 to-brand-600" />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">
            {showDetails
              ? "Cookie-indstillinger"
              : "Vi respekterer dit privatliv"}
          </h2>
          <button
            onClick={() => setShow(false)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Luk"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showDetails ? (
            <>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Vi bruger cookies til at forbedre din oplevelse på vores
                hjemmeside. Nødvendige cookies sikrer grundlæggende funktioner
                og kan ikke slås fra.
              </p>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Ved at klikke "Accepter alle" accepterer du brugen af alle
                cookie-kategorier. Læs mere i vores{" "}
                <a
                  href="/cookiepolitik"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline font-semibold"
                >
                  cookiepolitik
                </a>
                .
              </p>
            </>
          ) : (
            <>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Vælg hvilke cookies du ønsker at acceptere:
              </p>

              {/* Nødvendige cookies */}
              <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">
                    Nødvendige cookies
                  </h3>
                  <span className="text-sm text-slate-500">Altid aktiv</span>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Disse cookies er nødvendige for at hjemmesiden kan fungere
                  korrekt. De gemmer dine cookie-præferencer og sikrer
                  grundlæggende funktionalitet.
                </p>
              </div>

              {/* Statistik cookies */}
              <div className="mb-4 p-4 bg-slate-50 rounded-xl opacity-60">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">
                    Statistik-cookies
                  </h3>
                  <input type="checkbox" disabled className="w-5 h-5" />
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  <strong>Status: Ikke i brug.</strong> Hvis vi implementerer
                  statistik-cookies i fremtiden, vil du blive spurgt om
                  samtykke.
                </p>
              </div>

              {/* Marketing cookies */}
              <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">
                    Marketing-cookies
                  </h3>
                  <input
                    type="checkbox"
                    checked={marketingChecked}
                    onChange={(e) => setMarketingChecked(e.target.checked)}
                    className="w-5 h-5 accent-teal-600"
                  />
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Disse cookies bruges til at vise dig relevante annoncer på
                  Facebook og TikTok baseret på din aktivitet på vores
                  hjemmeside.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100">
          <div className="flex flex-wrap gap-3 justify-end">
            {!showDetails ? (
              <>
                <button
                  onClick={() => setShowDetails(true)}
                  className="btn btn-ghost btn-sm"
                >
                  Tilpas indstillinger
                </button>
                <button
                  onClick={handleRejectAll}
                  className="btn btn-ghost btn-sm"
                >
                  Kun nødvendige
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="btn btn-primary btn-sm"
                >
                  Accepter alle
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn btn-ghost btn-sm"
                >
                  Tilbage
                </button>
                <button
                  onClick={handleSaveCustom}
                  className="btn btn-primary btn-sm"
                >
                  Gem mine valg
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
